declare global {
  interface Window {
    Telegram: {
      WebApp: {
        initData: string;
        initDataUnsafe: {
          user?: {
            id: number;
            first_name?: string;
            last_name?: string;
            username?: string;
            photo_url?: string;
          };
          start_param?: string;
        };
        themeParams: Record<string, string>;
        colorScheme: "light" | "dark";
        ready: () => void;
        expand: () => void;
        isActive?: boolean;
        onEvent?: (
          eventType: "themeChanged" | "activated" | "deactivated",
          eventHandler: () => void,
        ) => void;
        offEvent?: (
          eventType: "themeChanged" | "activated" | "deactivated",
          eventHandler: () => void,
        ) => void;
        HapticFeedback: {
          selectionChanged?: () => void;
          notificationOccurred: (type: "success" | "error" | "warning") => void;
          impactOccurred: (
            style: "light" | "medium" | "heavy" | "rigid" | "soft",
          ) => void;
        };
        close: () => void;
        // Ссылки: openLink — внешние http(s) (опционально с instant view),
        // openTelegramLink — t.me/ ссылки внутри клиента Telegram.
        openLink: (
          url: string,
          options?: { try_instant_view?: boolean },
        ) => void;
        openTelegramLink: (url: string) => void;
      };
    };
  }
}

export function tgWebApp() {
  if (typeof window === "undefined" || !window.Telegram?.WebApp) {
    return null;
  }
  return window.Telegram.WebApp;
}

export function applyThemeFromTelegram(): void {
  const wa = tgWebApp();
  const root = document.documentElement;
  const button = wa?.themeParams?.button_color;
  const buttonText = wa?.themeParams?.button_text_color;
  const colorScheme = wa?.colorScheme ?? preferredColorScheme();

  if (button) root.style.setProperty("--tg-theme-button-color", button);
  if (buttonText)
    root.style.setProperty("--tg-theme-button-text-color", buttonText);
  root.dataset.tpTheme = colorScheme;
  root.style.colorScheme = colorScheme;
}

export function getInitData(): string {
  return tgWebApp()?.initData ?? "";
}

export function getTelegramUser() {
  return tgWebApp()?.initDataUnsafe?.user ?? null;
}

export function triggerHapticFeedback(): void {
  const haptic = tgWebApp()?.HapticFeedback;

  try {
    haptic?.selectionChanged?.();
  } catch {
    // Telegram can reject haptics on unsupported clients.
  }

  try {
    haptic?.impactOccurred?.("medium");
  } catch {
    // Browser vibration below is the fallback.
  }

  if (typeof navigator === "undefined") return;
  try {
    navigator.vibrate?.([18, 8, 18]);
  } catch {
    // Some WebViews expose vibrate but reject calls silently.
  }
}

export function getStartParam(): string | undefined {
  return (
    tgWebApp()?.initDataUnsafe?.start_param ??
    getLaunchParam("tgWebAppStartParam") ??
    parseParam(tgWebApp()?.initData ?? "", "start_param")
  );
}

function getLaunchParam(name: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  return (
    parseParam(window.location.search, name) ??
    parseParam(window.location.hash.replace(/^#/, ""), name)
  );
}

function parseParam(raw: string, name: string): string | undefined {
  if (!raw) return undefined;
  const params = new URLSearchParams(raw.replace(/^[?#]/, ""));
  return params.get(name) ?? undefined;
}

function preferredColorScheme(): "light" | "dark" {
  if (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: light)").matches
  ) {
    return "light";
  }
  return "dark";
}

// Открыть t.me/ ссылку внутри клиента Telegram (без выхода в браузер).
export function openTelegramLink(url: string): void {
  const target = normalizeTelegramUrl(url) ?? normalizeTelegramLink(url);
  const wa = tgWebApp();
  if (tryOpenTelegramLink(target, wa)) return;
  // Этот fallback сохраняет прежнее поведение ссылок ресурсов в клиентах,
  // где WebApp API отказывает на нестандартных invite/start ссылках.
  openHttpFallback(target, wa);
}

// Открыть внешнюю http(s) ссылку (например, success_url рекламодателя).
export function openExternalLink(url: string): void {
  openHttpFallback(url, tgWebApp());
}

// Ссылка возврата может вести как в бот / другую Mini App, так и на обычный
// сайт. Telegram-ссылки открываем нативно внутри клиента, остальные — в
// текущем WebView. WebApp.openLink здесь не подходит: он всегда запускает
// внешний браузер.
export function openReturnLink(url: string): void {
  const target = normalizeReturnLink(url);
  if (!target) return;

  const telegramTarget = normalizeTelegramUrl(target);
  if (telegramTarget) {
    if (tryOpenTelegramLink(telegramTarget, tgWebApp())) return;
    if (typeof window !== "undefined") {
      window.location.assign(telegramTarget);
    }
    return;
  }
  if (typeof window !== "undefined") {
    window.location.assign(target);
  }
}

// Заливщики возвращают пользователя в своего бота и пишут ссылку так, как её
// даёт Telegram: `tg://resolve?domain=bot&start=payload` или просто `@bot`.
// Раньше обе формы не проходили нормализацию, финальная кнопка навсегда
// оставалась заблокированной, и пользователь видел замок при 100% прогресса.
// Приводим их к https://t.me/… — эту форму openReturnLink открывает нативно.
function telegramShorthandToHttps(value: string): string | null {
  const username = /^@([A-Za-z0-9_]{4,64})$/.exec(value);
  if (username) return `https://t.me/${username[1]}`;

  if (!/^tg:\/\//i.test(value)) return null;
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return null;
  }
  // tg://resolve?domain=… — единственная форма, у которой есть точный
  // https-эквивалент. Остальные (tg://join, tg://settings) не преобразуем.
  const host = (
    parsed.host || parsed.pathname.replace(/^\/+/, "")
  ).toLowerCase();
  if (host !== "resolve") return null;
  const domain = parsed.searchParams.get("domain");
  if (!domain || !/^[A-Za-z0-9_]{4,64}$/.test(domain)) return null;
  const target = new URL(`https://t.me/${domain}`);
  for (const [key, entry] of parsed.searchParams.entries()) {
    if (key !== "domain") target.searchParams.set(key, entry);
  }
  return target.toString();
}

// Принимаем обычный абсолютный http(s), а для частого пользовательского ввода
// t.me/... достраиваем безопасный HTTPS-протокол. Telegram-host с портом или
// credentials отклоняем, чтобы он не маскировался под обычную внешнюю ссылку.
export function normalizeReturnLink(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  const fromTelegramShorthand = telegramShorthandToHttps(trimmed);
  if (fromTelegramShorthand) return normalizeTelegramUrl(fromTelegramShorthand);
  const candidate = /^(?:www\.)?(?:t\.me|telegram\.me)(?:[/?#]|$)/i.test(
    trimmed,
  )
    ? `https://${trimmed}`
    : trimmed;
  const target = normalizeAbsoluteHttpUrl(candidate);
  if (!target) return null;

  try {
    const hostname = new URL(target).hostname.toLowerCase();
    if (
      ["t.me", "www.t.me", "telegram.me", "www.telegram.me"].includes(hostname)
    ) {
      return normalizeTelegramUrl(target);
    }
  } catch {
    return null;
  }
  return target;
}

function tryOpenTelegramLink(
  url: string,
  wa: ReturnType<typeof tgWebApp>,
): boolean {
  if (!wa || !isTelegramUrl(url)) return false;
  try {
    wa.openTelegramLink(url);
    return true;
  } catch {
    return false;
  }
}

function isTelegramUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" && parsed.hostname === "t.me";
  } catch {
    return false;
  }
}

function normalizeTelegramUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();
    if (
      !["t.me", "www.t.me", "telegram.me", "www.telegram.me"].includes(
        hostname,
      ) ||
      !["http:", "https:"].includes(parsed.protocol) ||
      parsed.port ||
      parsed.username ||
      parsed.password
    ) {
      return null;
    }
    parsed.protocol = "https:";
    parsed.hostname = "t.me";
    return parsed.toString();
  } catch {
    return null;
  }
}

function normalizeAbsoluteHttpUrl(url: string): string | null {
  try {
    const parsed = new URL(url.trim());
    if (
      !["http:", "https:"].includes(parsed.protocol) ||
      parsed.username ||
      parsed.password
    ) {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
}

export function normalizeTelegramLink(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("@")) return `https://t.me/${trimmed.slice(1)}`;
  const privateChannelId = botApiChannelIdToMtproto(trimmed);
  if (privateChannelId) return `https://t.me/c/${privateChannelId}/1`;
  return `https://t.me/${trimmed}`;
}

function botApiChannelIdToMtproto(value: string): string | null {
  if (!/^-\d+$/.test(value)) return null;
  try {
    const botApiId = BigInt(value);
    const channelId = -botApiId - 1_000_000_000_000n;
    return channelId >= 1n && channelId <= 997_852_516_352n
      ? channelId.toString()
      : null;
  } catch {
    return null;
  }
}

function openHttpFallback(url: string, wa: ReturnType<typeof tgWebApp>): void {
  if (!url) return;
  if (wa && /^https?:\/\//i.test(url)) {
    try {
      wa.openLink(url);
      return;
    } catch {
      // Последний fallback ниже.
    }
  }
  if (typeof window === "undefined") return;
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (!opened) {
    window.location.assign(url);
  }
}
